import type { MagazineTagForDesignMagazineEditQuery } from "@/lib/gql/__generated__/graphql";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";

import * as Model from "../model";
import {
	AboutRecommendedTagFeedback,
	CitySelectField,
	FinishButton,
	LocationTagSection,
	RecommendedTagLabelList,
	RecommendedTagListSection,
	RegionWithPrefectureSelectField,
	SelectedTagLabelList,
	SelectedTagListSection,
	TagInputField,
} from "./components";

type onFinishCallbackProps = {
	tagList: Array<string>;
	regionTag?: string;
	prefectureTag?: string;
	cityTag?: string;
};
type Props = {
	tagList?: Array<string>;
	regionTag?: string;
	prefectureTag?: string;
	cityTag?: string;
	//
	open: boolean;
	onOpenChange: (open: boolean) => void;
	//
	onFinishCallback: (props: onFinishCallbackProps) => void;

	magazineTagQueryData: MagazineTagForDesignMagazineEditQuery;
};

export const DesignMagazineSettingTagPresenter = ({
	tagList,
	regionTag,
	prefectureTag,
	cityTag,
	open,
	onOpenChange,
	onFinishCallback,
	magazineTagQueryData,
}: Props) => {
	const {
		//
		// readonly
		viewData,
		//
		// changeable
		viewModel,
		// タグ選択時の処理
		onSetSelectedTag,
		// タグ削除時の処理
		onRemoveSelectedTag,
		// タグの一括削除時の処理
		onRemoveAllSelectedTag,
		// 都道府県タグ選択時の処理
		onSetSelectedRegionWithPrefectureTag,
		// 都市タグ選択時の処理
		onSetSelectedCityTag,
		// 地名タグの一括削除時の処理
		onRemoveLocationTag,
		//
		// 完了時の処理
		onFinish,
	} = Model.UseCase.useInitialized({
		tagList,
		regionTag,
		prefectureTag,
		cityTag,
		onOpenChange,
		onFinishCallback,
		magazineTagQueryData,
	});

	return (
		<Dialog modal open={open} onOpenChange={onOpenChange}>
			<DialogContent className="md:max-w-1xl overflow-y-scroll">
				<DialogHeader>
					<DialogTitle>タグの設定</DialogTitle>
				</DialogHeader>

				<div className="border-b border-zinc-300" />

				<div className="space-y-3">
					{/* 選択中のタグ */}
					<SelectedTagListSection
						onRemoveLocationTag={onRemoveAllSelectedTag}
						tagListChildren={
							<div className="flex flex-wrap gap-3">
								{/* 選択中のタグ一覧 */}
								<SelectedTagLabelList
									selectedTagList={viewModel.selectedTagList}
									onSelectCallback={onRemoveSelectedTag}
								/>
							</div>
						}
						freeWordTagChildren={
							viewModel.canSelectTag && <TagInputField onEnterCallback={onSetSelectedTag} />
						}
					/>

					{/* おすすめのタグ */}
					<RecommendedTagListSection
						feedbackChildren={<AboutRecommendedTagFeedback />}
						recommendedTagListChildren={
							<div className="flex flex-wrap gap-3 p-3">
								<RecommendedTagLabelList
									recommendedTagList={viewData.recommendedTagList}
									selectedTagList={viewModel.selectedTagList}
									canSelectTag={viewModel.canSelectTag}
									onSelectCallback={onSetSelectedTag}
								/>
							</div>
						}
					/>
				</div>

				{/* 地名タグ */}
				<LocationTagSection
					onRemoveLocationTag={onRemoveLocationTag}
					regionWithPrefectureSelectFieldChildren={
						<RegionWithPrefectureSelectField
							regionWithPrefectureTagList={viewData.regionWithPrefectureTagList}
							selectedPrefectureTag={viewModel.selectedPrefectureTag}
							onSelect={onSetSelectedRegionWithPrefectureTag}
						/>
					}
					citySelectFieldChildren={
						viewModel.selectedPrefectureTag && (
							<CitySelectField
								cityTagList={viewData.cityTagList}
								selectedCityTag={viewModel.selectedCityTag}
								onSelect={onSetSelectedCityTag}
							/>
						)
					}
				/>

				<div className="border-b border-zinc-300" />

				<div className="text-right">
					<FinishButton onClickCallback={onFinish} />
				</div>
			</DialogContent>
		</Dialog>
	);
};
